import React, { useState, useEffect, useRef } from "react";
import { MotionConfig, useScroll, useTransform } from "framer-motion";
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import Navbar from './components/Navbar/Navbar';
import Hero from './components/Hero/Hero';
import About from './components/About/About';
import Journey from './components/Journey/Journey';
import Skills from './components/Skills/Skills';
import Projects from './components/Projects/Projects';
import Certificates from './components/Certificates/Certificates';
import Contact from './components/Contact/Contact';
import Footer from './components/Footer/Footer';
import ThankYou from './components/ThankYou';
import portfolioProjects from './data/projects';

const AppContent = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [darkMode, setDarkMode] = useState(() => {
    // Check for saved theme preference or use system preference
    if (typeof window !== 'undefined') {
      const savedMode = localStorage.getItem('darkMode');
      if (savedMode !== null) return savedMode === 'true';
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const [contactStatus, setContactStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  // Apply dark mode: Tailwind's `dark:` classes key off the `dark` class,
  // while a couple of plain-CSS rules (App.css) key off `data-theme`.
  // Both are kept in sync here so either styling approach works.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  // Toggle dark mode
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };


  // Scroll handler
  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    if (section) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      section.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }
    setMobileMenuOpen(false);
  };

  // Track scroll position and active section
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      const sections = ['home', 'about', 'experience', 'skills', 'projects', 'certificates', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Navbar links data - Added certificates
  const navLinks = [
    { id: "about", label: "About" },
    { id: "experience", label: "Experience" },
    { id: "skills", label: "Skills" },
    { id: "projects", label: "Projects" },
    { id: "certificates", label: "Certificates" },
    { id: "contact", label: "Contact" }
  ];
  const projects = portfolioProjects;

  const certificates = [
    {
      title: "Software Engineer Intern",
      issuer: "Paperbill Tech Pvt. Ltd.",
      date: "October 2025",
      link: "https://drive.google.com/file/d/1s84iiRcJwmYdCAtzyefKDZoHeiGT0fyp/view?usp=sharing",
      verified: true,
      logo: "https://paperbill.in/icons/icon-512x512.png"
    },

    {
      title: "NPTEL Certification",
      issuer: "NPTEL",
      date: "2025",
      link: "https://drive.google.com/file/d/1ciPcFMVnQwhvYpBf3ml4HplUZhRjlX75/view?usp=sharing",
      verified: true,
      // logo: "https://upload.wikimedia.org/wikipedia/commons/9/92/NPTEL_logo.png"
      logo: "/projects/nptel.webp"
    },

    {
      title: "MOOC Certification",
      issuer: "MOOC",
      date: "2025",
      link: "https://drive.google.com/file/d/11-CI2i8W8nvETzDOxNOLH0Vfl7tk-ydZ/view?usp=sharing",
      verified: true,
      logo: "https://cdn-icons-png.flaticon.com/512/3135/3135810.png"
    },
    {
      title: "Research Paper Publication – BudgetBuddy",
      issuer: "International Journal of Combined Research & Development (IJCRD)",
      date: "August 2025",
      link: "https://drive.google.com/file/d/1dceKL_Wiy5buugdz0CAjL2wov0RBgZ4-/view?usp=sharing", // optional: replace if you have drive link
      verified: true,
      logo: "https://cdn-icons-png.flaticon.com/512/3135/3135755.png"
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    setContactStatus(null);
    setIsSubmitting(true);

    if (
      !process.env.REACT_APP_EMAILJS_SERVICE_ID ||
      !process.env.REACT_APP_EMAILJS_TEMPLATE_ID ||
      !process.env.REACT_APP_EMAILJS_PUBLIC_KEY
    ) {
      setContactStatus({
        type: 'error',
        message: 'The message service is not configured right now. Please email me directly instead.'
      });
      setIsSubmitting(false);
      return;
    }

    const templateParams = {
      name: form.name.value,
      email: form.email.value,
      title: form.title.value,
      message: form.message.value,
      to_name: "Shambhavi Singh",
      reply_to: form.email.value,
      date: new Date().toLocaleString()
    };

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      form.reset();
      setContactStatus({
        type: 'success',
        message: 'Thanks for reaching out. Your message has been sent, and I’ll get back to you soon.'
      });
    } catch {
      setContactStatus({
        type: 'error',
        message: 'Your message could not be sent. Please try again or email me directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <>
      <main
        role="main"
        className="relative bg-gradient-to-b from-indigo-50 to-white dark:from-gray-900 dark:to-gray-950 text-gray-800 dark:text-gray-100 font-sans min-h-screen transition-colors duration-300"
      >

        {/* Background gradient */}
        <div className="fixed inset-0 -z-10 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-indigo-200/20 via-white to-purple-200/20 dark:from-indigo-900/20 dark:via-gray-950 dark:to-purple-900/20 transition-colors duration-300"></div>
        </div>

        {/* Floating circles */}
        <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-indigo-300/20 dark:bg-indigo-900/20 blur-3xl transition-colors duration-300"></div>
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full bg-purple-300/20 dark:bg-purple-900/20 blur-3xl transition-colors duration-300"></div>
        </div>

        {/* Navbar */}
        <Navbar mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} scrolled={scrolled} activeSection={activeSection} darkMode={darkMode} toggleDarkMode={toggleDarkMode} scrollToSection={scrollToSection} navLinks={navLinks} />

        {/* Hero Section */}
        <Hero heroRef={heroRef} yBg={yBg} scrollToSection={scrollToSection} />

        {/* About Section */}
        <About />

        {/* Experience Section */}
        <Journey />

        {/* Skills Section */}
        <Skills darkMode={darkMode} />

        {/* Projects Section */}
        <Projects projects={projects} />

        {/* Certificates Section */}
        <Certificates certificates={certificates} />

        {/* Contact Section */}
        <Contact handleSubmit={handleSubmit} contactStatus={contactStatus} isSubmitting={isSubmitting} />

        {/* Footer */}
        <Footer />
      </main>
    </>
  );
};


const App = () => {
  return (
    <MotionConfig reducedMotion="user">
      <Router>
        <Routes>
          <Route path="/" element={<AppContent />} />
          <Route path="/thank-you" element={<ThankYou />} />
        </Routes>
      </Router>
    </MotionConfig>
  );
};

export default App;
